import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import {
  useActivateProjectMutation,
  useGetConfigurationSettingsPromptQuery,
  useGetCustomTemplatesQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useGetProjectByIdQuery,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import { CreateProjectManualStep4 } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CreateProjectManualStep4';
import { CreateProjectPromptBuilderStep4 } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep4Page/CreateProjectPromptBuilderStep4';
import { clsx } from 'clsx';
import { API_VERSION } from 'src/constants/api-routes';
import { useNotification } from 'src/hooks/useNotification';
import type { ConfigurationSettings } from 'src/services/configurationService';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type {
  ConfigurationFormFieldsObject,
  IndexedFormField,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import {
  toFormFieldsObject,
  withFieldIndex,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import { usePagedConfigurationSettings } from 'src/pages/ConfigurationPage/hooks/usePagedConfigurationSettings';
import { getApiVersion } from 'src/utils/apiVersion';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-60'} />
    </div>
  );
};

const CreateProjectStep4Page = () => {
  const [activeSettings, setActiveSettings] = useState<ConfigurationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManual, setIsManual] = useState(null);
  const [inputData, setInputData] = useState<PersonaInputData | null>(null);
  const [formFields, setFormFields] = useState<IndexedFormField[] | null>(null);
  const [formFieldsObject, setFormFieldsObject] = useState<ConfigurationFormFieldsObject>({});
  const notification = useNotification();
  const { id, step } = useParams();
  const loader = useLoader();
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useAppDispatch();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const [activateProject, { isLoading: isLoadingActivateProject }] = useActivateProjectMutation();
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery();
  const { settings, isLoadingAllConfigurationSettings } = usePagedConfigurationSettings(
    id,
    { skip: !(id || defaultTemplateFields), refetchOnMountOrArgChange: true },
    null,
  );
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings?.id },
      {
        skip: !(id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );
  const { data: customTemplatesData, isLoading: isLoadingCustomTemplates } =
    useGetCustomTemplatesQuery();

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingCustomTemplates ||
      isLoadingActivateProject ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingCustomTemplates,
    isLoadingActivateProject,
    isManual,
    settings,
  ]);

  useEffect(() => {
    if (currentProject && !currentProject.is_active) {
      activateProject({ projectId: id }).then((activateProjectResult) => {
        if (!('error' in activateProjectResult)) {
          notification.success('Project has been activated!');
        }
      });
    }
  }, [currentProject]);

  useEffect(() => {
    if (defaultTemplateFields) {
      const fields = withFieldIndex(defaultTemplateFields.form_fields);
      setFormFields(fields);
      setInputData(null);
    }
  }, [defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = toFormFieldsObject(formFields);

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (settings) {
      const config = creationFlowState[id] || {};
      const settingsId = isManual ? config.manualSettingsId : config.promptBuilderSettingsId;

      if (settings.length) {
        if (settingsId) {
          const setting = settings.find((setting) => `${setting.id}` === `${settingsId}`);

          setting && setActiveSettings(setting);
        }
      }
    }
  }, [settings, isManual, creationFlowState]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = withFieldIndex(configurationSettingsPrompt.form_fields);

      setFormFields(fields);
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config?.promptType) {
      setIsManual(config.promptType === 'manual');
    }

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, [creationFlowState]);

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <CreateProjectStepsTitle
            step={isV2 ? +step - 1 : +step}
            title={isManual ? 'System Prompt' : 'AI Role'}
          />
          <CreateProjectHead step={+step} titleIndex={isManual ? 3 : 4} />
          <div
            className={clsx('flex flex-col w-full', isManual ? 'max-w-[500px]' : 'max-w-[455px]')}
          >
            {isManual ? (
              <CreateProjectManualStep4 customTemplatesData={customTemplatesData} />
            ) : (
              <CreateProjectPromptBuilderStep4
                activeSettings={activeSettings}
                inputData={inputData}
                defaultTemplateFields={defaultTemplateFields}
                formFieldsObject={formFieldsObject}
              />
            )}
          </div>
        </>
      )}
    </PageContentWrapper>
  );
};

export default CreateProjectStep4Page;
